import {Inject, Injectable} from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import {Observable, throwError} from 'rxjs';
import {catchError} from 'rxjs/operators';
import {ApiBaseUrl} from '../services/api.client';
import {NotificationService} from '../services/NotificationService';
import {SecurityService} from '../services/SecurityService';
import {Router} from '@angular/router';

@Injectable()
export class ApiTokenInterceptor implements HttpInterceptor {

   constructor(private _notificationService: NotificationService,
               private _securityService: SecurityService,
               private _router: Router,
               @Inject(ApiBaseUrl) private baseUrl?: string
               ) {
   }

   intercept(req: HttpRequest<any>, next: HttpHandler)
       : Observable<HttpEvent<any>> {

       const url = req.url.toLowerCase();

       if (!this.checkUrl(url)) {
           return next.handle(req);
       }

       // Cookie auth: the session is carried by the HttpOnly `ea_access_token`
       // cookie, so the app sends credentials and attaches no Authorization
       // header. There is no JS-readable token to decode for expiry — the API
       // is authoritative for expiry and signals it via X-Token-Invalid below.
       req = req.clone({ withCredentials: true });

       return next.handle(req).pipe(
           catchError((error: HttpEvent<any>) => {
               // The /me session probe (rehydration on app start) is exempt: a 401 there
               // just means "no valid session yet", which the rehydration flow handles by
               // resolving false and letting AuthGuard route to /login. Forcing a logout +
               // "session no longer valid" toast for it would fire spuriously on a normal
               // logged-out load. Every other endpoint keeps the X-Token-Invalid behaviour.
               if (this.isAuthTokenFailure(error) && !this.isSessionProbe(url)) {
                   this._notificationService.showError('Your session is no longer valid. Please log in again.');
                   this._securityService.logout();
                   // sessionExpired rides along as navigation state (not a query param,
                   // so it can't be bookmarked/shared) and makes the login page show
                   // its "session expired" banner — the toast above is easily missed
                   // because component error toasts replace it (SweetAlert singleton).
                   this._router.navigate(['/login'], { state: { sessionExpired: true } }).then();
               }
               return throwError(() => error);
           })
       );
   }

   private isSessionProbe(url: string): boolean {
       return url.endsWith('/api/v1/security/me');
   }

   // Only force a re-login when the API tells us the session itself is invalid
   // or expired (via the X-Token-Invalid header). Endpoint-specific 401s are left alone.
   private isAuthTokenFailure(error: HttpEvent<any>): boolean {
       return error instanceof HttpErrorResponse
           && error.status === 401
           && error.headers?.has('X-Token-Invalid') === true;
   }

    private checkUrl(url: string): boolean {
        return url.startsWith(this.baseUrl) && !url.startsWith(`${this.baseUrl}/api/v1/public/`);
    }
}
